import { useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import ScrollToTopButton from '../../components/feature/ScrollToTopButton';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const SCHEMA_SERVICOS = JSON.stringify({
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': `${import.meta.env.VITE_SITE_URL}/servicos#webpage`,
  url: `${import.meta.env.VITE_SITE_URL}/servicos`,
  name: 'Serviços de Engenharia Sorocaba Campinas | AVCB, PGR e Perícias — Engeporto',
  description: 'Conheça os serviços da Engeporto: Engenharia de Prevenção de Incêndio (AVCB, CLCB), Segurança do Trabalho (PGR, LTCAT) e Avaliações e Perícias Técnicas em Sorocaba e Campinas.',
  inLanguage: 'pt-BR',
  isPartOf: { '@id': `${import.meta.env.VITE_SITE_URL}/#website` },
  mainEntity: {
    '@type': 'ItemList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Engenharia de Prevenção de Incêndio',
        url: `${import.meta.env.VITE_SITE_URL}/servicos/prevencao-incendio`,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Engenharia de Segurança do Trabalho',
        url: `${import.meta.env.VITE_SITE_URL}/servicos/seguranca-trabalho`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: 'Engenharia de Avaliações e Perícias',
        url: `${import.meta.env.VITE_SITE_URL}/servicos/avaliacoes-pericias`,
      },
    ],
  },
});

const ServicosPage = () => {
  useEffect(() => {
    document.title = 'Serviços de Engenharia Sorocaba Campinas | AVCB, PGR e Perícias — Engeporto';
    const setMeta = (name: string, content: string, prop = false) => {
      const sel = prop ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      const el = document.querySelector(sel) as HTMLMetaElement | null;
      if (el) el.setAttribute('content', content);
    };
    setMeta('description', 'Conheça os serviços da Engeporto: Prevenção de Incêndio (AVCB, CLCB), Segurança do Trabalho (PGR, LTCAT) e Avaliações e Perícias Técnicas nas Regiões Metropolitanas de Sorocaba e Campinas.');
    setMeta('keywords', 'serviços engenharia Sorocaba, AVCB Campinas, CLCB, PGR, LTCAT, perícia técnica, segurança do trabalho');
    setMeta('og:title', 'Serviços de Engenharia | AVCB, PGR e Perícias — Engeporto Sorocaba Campinas', true);
    setMeta('og:description', 'Prevenção de Incêndio, Segurança do Trabalho e Perícias Técnicas em Sorocaba e Campinas.', true);
    setMeta('og:url', `${import.meta.env.VITE_SITE_URL}/servicos`, true);

    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL}/servicos`);
    return () => { canonical?.remove(); };
  }, []);

  const servicos = [
    {
      icon: 'ri-fire-line',
      title: 'Engenharia de Prevenção de Incêndio',
      subtitle: 'Projetos AVCB, CLCB e Eletrotécnica Aplicada',
      description: 'Soluções completas em prevenção e combate a incêndio, desde projetos para emissão de AVCB e CLCB até consultoria técnica especializada e treinamentos de brigada.',
      image: 'https://public.readdy.ai/ai/img_res/2182ac830e2223fd5e4d96116d2bfe62.jpg',
      link: '/servicos/prevencao-incendio',
      destaque: [
        'Projetos AVCB e CLCB',
        'Regularização Bombeiros',
        'Laudos Elétricos',
        'Treinamento de Brigada',
      ],
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Engenharia de Segurança do Trabalho',
      subtitle: 'PGR, LTCAT e Conformidade com NRs',
      description: 'Elaboração de programas e laudos técnicos conforme Normas Regulamentadoras, gestão de riscos ocupacionais e implantação de cultura de prevenção de acidentes.',
      image: 'https://readdy.ai/api/search-image?query=workplace%20safety%20scene%20with%20workers%20wearing%20personal%20protective%20equipment%20hard%20hats%20safety%20vests%20in%20industrial%20facility%20with%20safety%20signs%20and%20equipment%20professional%20occupational%20safety%20environment&width=800&height=600&seq=serv002&orientation=landscape',
      link: '/servicos/seguranca-trabalho',
      destaque: [
        'Elaboração de PGR',
        'LTCAT',
        'Laudos de Insalubridade',
        'Treinamentos NRs',
      ],
    },
    {
      icon: 'ri-file-list-3-line',
      title: 'Engenharia de Avaliações e Perícias',
      subtitle: 'Perícias Judiciais e Avaliações Técnicas',
      description: 'Perícias técnicas judiciais e extrajudiciais, avaliações patrimoniais, laudos especializados e pareceres técnicos com rigor técnico e imparcialidade.',
      image: 'https://readdy.ai/api/search-image?query=professional%20engineer%20conducting%20technical%20inspection%20with%20clipboard%20and%20measuring%20equipment%20examining%20building%20structure%20and%20taking%20notes%20formal%20technical%20assessment%20scene%20in%20commercial%20property&width=800&height=600&seq=serv003&orientation=landscape',
      link: '/servicos/avaliacoes-pericias',
      destaque: [
        'Perícias Judiciais',
        'Assistência Técnica',
        'Avaliações de Imóveis',
        'Laudos Especializados',
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: SCHEMA_SERVICOS }}
      />
      <Navbar />
      
      <section className="pt-36 pb-12 lg:pt-32 lg:pb-20 bg-gradient-to-br from-[#0A1628] via-[#0D1B33] to-[#0A1628] relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }}></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] px-4 py-2 rounded-full text-xs lg:text-sm font-semibold mb-6 lg:mb-8">
              <i className="ri-star-line text-sm lg:text-base"></i>
              SOLUÇÕES TÉCNICAS ESPECIALIZADAS
            </div>
            
            <h1 className="font-display font-bold text-3xl lg:text-5xl xl:text-6xl text-white mb-4 lg:mb-6">
              Nossos Serviços
            </h1>
            <p className="text-sm lg:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Três pilares fundamentais da engenharia para garantir segurança, conformidade e excelência técnica
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-12 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="space-y-16 lg:space-y-32">
            {servicos.map((servico, index) => (
              <motion.div
                key={servico.title}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="inline-flex items-center gap-2 lg:gap-3 bg-[#D4AF37]/10 text-[#D4AF37] px-3 py-2 lg:px-4 rounded-full text-xs lg:text-sm font-bold mb-4 lg:mb-6">
                    <i className={`${servico.icon} text-base lg:text-lg`}></i>
                    PILAR {index + 1}
                  </div>
                  
                  <h2 className="font-display font-bold text-2xl lg:text-4xl xl:text-5xl text-[#0A1628] mb-3 lg:mb-4 leading-tight">
                    {servico.title}
                  </h2>
                  
                  <p className="text-[#D4AF37] font-bold text-base lg:text-lg mb-4 lg:mb-6">
                    {servico.subtitle}
                  </p>
                  
                  <p className="text-sm lg:text-lg text-gray-700 leading-relaxed mb-6 lg:mb-10">
                    {servico.description}
                  </p>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 lg:gap-4 mb-6 lg:mb-10">
                    {servico.destaque.map((item) => (
                      <div key={item} className="flex items-start gap-2 lg:gap-3">
                        <div className="w-4 h-4 lg:w-5 lg:h-5 flex items-center justify-center bg-[#D4AF37] rounded-full flex-shrink-0 mt-0.5">
                          <i className="ri-check-line text-white text-[10px] lg:text-xs"></i>
                        </div>
                        <span className="text-xs lg:text-sm text-gray-700 font-medium">{item}</span>
                      </div>
                    ))}
                  </div>

                  <Link
                    to={servico.link}
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#0A1628] px-6 py-3 lg:px-8 lg:py-4 rounded-lg text-sm lg:text-base font-bold hover:bg-[#C4A030] transition-all whitespace-nowrap"
                  >
                    Ver Detalhes Completos
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>

                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl">
                    <img
                      src={servico.image}
                      alt={servico.title}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 lg:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-10 lg:mb-16"
          >
            <h2 className="font-display font-bold text-2xl lg:text-4xl xl:text-5xl text-[#0A1628] mb-3 lg:mb-4">
              Por Que Escolher a Engeporto?
            </h2>
            <p className="text-sm lg:text-lg text-gray-600 max-w-2xl mx-auto">
              Diferenciais que nos tornam referência em engenharia especializada
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-8">
            {[
              {
                icon: 'ri-team-line',
                title: 'Equipe Especializada',
                description: 'Engenheiros qualificados com certificações técnicas',
              },
              {
                icon: 'ri-time-line',
                title: 'Prazos Garantidos',
                description: 'Compromisso com entregas dentro do prazo acordado',
              },
              {
                icon: 'ri-shield-check-line',
                title: 'Conformidade Total',
                description: 'Projetos e laudos conforme normas vigentes',
              },
              {
                icon: 'ri-customer-service-2-line',
                title: 'Suporte Completo',
                description: 'Acompanhamento em todas as etapas do projeto',
              },
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className="bg-white rounded-xl p-6 lg:p-8 shadow-lg text-center hover:shadow-xl transition-shadow"
              >
                <div className="w-12 h-12 lg:w-16 lg:h-16 flex items-center justify-center bg-[#D4AF37]/10 rounded-xl mx-auto mb-4 lg:mb-6">
                  <i className={`${item.icon} text-2xl lg:text-3xl text-[#D4AF37]`}></i>
                </div>
                <h3 className="font-display font-bold text-base lg:text-xl text-[#0A1628] mb-2 lg:mb-3">
                  {item.title}
                </h3>
                <p className="text-xs lg:text-base text-gray-600 leading-relaxed">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 lg:py-24 bg-gradient-to-br from-[#0A1628] via-[#0D1B33] to-[#0A1628] relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }}></div>
        </div>
        
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-display font-bold text-2xl lg:text-4xl xl:text-5xl text-white mb-4 lg:mb-6">
              Precisa de uma Consultoria Técnica?
            </h2>
            <p className="text-sm lg:text-xl text-gray-300 mb-8 lg:mb-10">
              Fale com nossos especialistas e receba um orçamento personalizado
            </p>
            <a
              href={`https://wa.me/5515996719308?text=${encodeURIComponent('Olá! Vim pela página de Serviços e gostaria de solicitar uma consultoria técnica.')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 lg:gap-3 bg-[#D4AF37] text-[#0A1628] px-6 py-4 lg:px-10 lg:py-5 rounded-lg text-sm lg:text-lg font-bold hover:bg-[#C4A030] transition-all shadow-xl whitespace-nowrap cursor-pointer"
            >
              <i className="ri-whatsapp-line text-xl lg:text-2xl"></i>
              Solicitar Orçamento
            </a>
          </motion.div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
      <ScrollToTopButton />
    </div>
  );
};

export default ServicosPage;